import { Switch, Route, Redirect } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import ScrollToTop from "@/components/utils/ScrollToTop";
import NotFound from "@/pages/not-found";
import { Loader2 } from "lucide-react";

// Public pages
import HomePage from "@/pages/home-page";
import AboutPage from "@/pages/about-page";
import ProgramsPage from "@/pages/programs-page";
import GetHelpPage from "@/pages/get-help-page";
import HireFromUsPage from "@/pages/hire-from-us-page";
import GetInvolvedPage from "@/pages/get-involved-page";
import SuccessStoriesPage from "@/pages/success-stories-page";
import SuccessStoryDetailPage from "@/pages/success-story-detail";
import ContactPage from "@/pages/contact-page";
import AuthPage from "@/pages/auth-page";
import CareerCoachPage from "@/pages/career-coach-page";
import ResearchPage from "@/pages/research-page";
import ImpactReportPage from "@/pages/impact-report-page";
import EconomicImpactSimulator from "@/pages/economic-impact-simulator";
import DebugMobilePage from "@/pages/debug-mobile";
import TestDeploymentPage from "@/pages/test-deployment";
import P2PPage from "@/pages/p2p-page";

// Dashboard pages
import ClientDashboard from "./pages/dashboard/client-dashboard";
import CaseWorkerDashboard from "./pages/dashboard/case-worker-dashboard";
import AdminDashboard from "./pages/dashboard/admin-dashboard";
import EmployerDashboard from "./pages/dashboard/employer-dashboard";

import { ProtectedRoute } from "@/lib/protected-route";
import { useAuth } from "./hooks/use-auth";

// Separate dashboard redirect component using AuthProvider
function DashboardRedirect() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/auth" />;
  }

  switch (user.role) {
    case "client":
      return <Redirect to="/dashboard/client" />;
    case "caseWorker":
      return <Redirect to="/dashboard/case-worker" />;
    case "admin":
      return <Redirect to="/dashboard/admin" />;
    case "employer":
      return <Redirect to="/dashboard/employer" />;
    default:
      return <Redirect to="/auth" />;
  }
}

function Router() {
  return (
    <Switch>
      {/* Public Pages */}
      <Route path="/" component={HomePage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/programs" component={ProgramsPage} />
      <Route path="/get-help" component={GetHelpPage} />
      <Route path="/hire-from-us" component={HireFromUsPage} />
      <Route path="/get-involved" component={GetInvolvedPage} />
      <Route path="/research" component={ResearchPage} />
      <Route path="/impact-report" component={ImpactReportPage} />
      <Route path="/economic-impact-simulator" component={EconomicImpactSimulator} />
      <Route path="/debug-mobile" component={DebugMobilePage} />
      <Route path="/test-deployment" component={TestDeploymentPage} />
      <Route path="/p2p-vpsa" component={P2PPage} />
      <Route path="/success-stories" component={SuccessStoriesPage} />
      <Route path="/success-stories/:id" component={SuccessStoryDetailPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/auth" component={AuthPage} />
      <Route path="/career-coach" component={CareerCoachPage} />
      
      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" component={DashboardRedirect} />
      <ProtectedRoute path="/dashboard/client" component={ClientDashboard} />
      <ProtectedRoute path="/dashboard/case-worker" component={CaseWorkerDashboard} />
      <ProtectedRoute path="/dashboard/admin" component={AdminDashboard} />
      <ProtectedRoute path="/dashboard/employer" component={EmployerDashboard} />
      
      {/* Fallback to 404 */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <>
      <Router />
      <ScrollToTop />
      <Toaster />
    </>
  );
}

export default App;
